(function () {
  const PREVIEW_LOOP_SECONDS = 4;

  function parsePreviewStart(video) {
    if (!video) return 0;
    const raw = video.dataset.previewStart;
    if (raw === undefined || raw === "") return 0;
    const value = parseFloat(raw);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  function getPreviewLoopEnd(video) {
    return parsePreviewStart(video) + PREVIEW_LOOP_SECONDS;
  }

  function seekPreviewToLoopStart(video) {
    if (!video) return;
    const start = parsePreviewStart(video);
    try {
      video.currentTime = start;
    } catch (error) {
      /* ignore seek issues */
    }
  }

  function syncPreviewLoopPosition(video) {
    if (!video) return;
    const start = parsePreviewStart(video);
    const end = start + PREVIEW_LOOP_SECONDS;
    if (video.currentTime < start || video.currentTime >= end) {
      seekPreviewToLoopStart(video);
    }
  }

  function initUGCSlider(sectionEl) {
    if (!sectionEl || sectionEl.dataset.initialized === "true") {
      return;
    }

    const track = sectionEl.querySelector(".ugc-slider__track");
    // Les modals « Achetez le look » sont dans `.ugc-slider__modals`, hors de
    // `.ugc-slider` — on cible la section Shopify parente pour les lier au JS.
    const sectionRoot =
      sectionEl.closest(".shopify-section") || sectionEl.parentElement;
    const ugcShopSectionKey = sectionRoot ? sectionRoot.id : "";
    // Structure simplifiée : le viewport (clipping + mesure) est désormais
    // l'élément .ugc-slider lui-même ; seul le track reste comme couche mobile.
    const viewport = sectionEl;
    if (!track || !viewport) return;

    const supportsIntersectionObserver =
      typeof IntersectionObserver === "function";
    const preventMediaDrag = function (event) {
      if (event.target.closest(".ugc-card")) {
        event.preventDefault();
      }
    };
    let previewObserver = supportsIntersectionObserver
      ? new IntersectionObserver(handlePreviewIntersection, {
          root: viewport,
          threshold: 0,
          rootMargin: "40px",
        })
      : null;
    const observedPreviews = supportsIntersectionObserver
      ? new WeakSet()
      : null;
    let isSectionVisible = true;

    track.addEventListener("dragstart", preventMediaDrag);

    let baseCards = Array.from(track.querySelectorAll(".ugc-card"));

    const designMode =
      typeof window.Shopify !== "undefined" &&
      Boolean(window.Shopify.designMode);
    const editorSliderAnimation =
      sectionEl.getAttribute("data-editor-slider-animation") === "true";

    if (!baseCards.length) return;

    if (designMode && !editorSliderAnimation) {
      sectionEl.dataset.initialized = "true";
      track.querySelectorAll("img, video").forEach(function (mediaEl) {
        mediaEl.setAttribute("draggable", "false");
      });
      preparePreviewVideos(sectionEl);
      ensureShopProductImagesLoaded(track);
      return;
    }

    sectionEl.dataset.initialized = "true";

    track.querySelectorAll("img, video").forEach(function (mediaEl) {
      mediaEl.setAttribute("draggable", "false");
    });

    const computed = window.getComputedStyle(sectionEl);
    const gapValue = parseFloat(computed.getPropertyValue("--ugc-gap")) || 0;
    const autoSpeed = parseFloat(sectionEl.dataset.autoSpeed || "0");

    baseCards.forEach(function (card, index) {
      card.dataset.base = "true";
      card.dataset.cardIndex = String(index);
    });

    const mediaCount = baseCards.length;
    let mediaWidth = baseCards[0]
      ? baseCards[0].getBoundingClientRect().width
      : 0;
    let mediaHeight = baseCards[0]
      ? baseCards[0].getBoundingClientRect().height
      : 0;

    const templateCards = baseCards.map(function (card) {
      const clone = card.cloneNode(true);
      cleanCardNode(clone);
      clone.dataset.cardIndex = card.dataset.cardIndex || "";
      return clone;
    });

    sectionEl._ugcEditorCardHost = null;
    if (designMode && editorSliderAnimation) {
      const host = document.createElement("div");
      host.className = "ugc-slider__editor-card-host";
      host.setAttribute("data-ugc-editor-card-host", "");
      sectionEl.appendChild(host);
      baseCards.forEach(function (card) {
        host.appendChild(card);
      });
      sectionEl._ugcEditorCardHost = host;
    }

    let groupsState = [];
    let groupSpan = 0;
    let totalSpan = 0;
    let usingVariableSpans = false;
    let position = 0;
    let currentSpeed = autoSpeed ? -autoSpeed : 0;
    let targetSpeed = currentSpeed;
    const ease = 0.08;
    const TOUCH_ACTION_DEFAULT = "pan-y pinch-zoom";
    const TOUCH_ACTION_LOCKED = "none";
    let pointerActive = false;
    let pointerDragging = false;
    let pointerId = null;
    let pointerStartX = 0;
    let lastPointerX = 0;
    let pointerStartY = 0;
    let lastPointerY = 0;
    let pointerCaptured = false;
    let pointerLastTime = 0;
    let pointerVelocityX = 0;
    let pointerTotalTravelX = 0;
    let momentumVelocity = 0;
    const MOMENTUM_DECAY = 0.92;
    const MOMENTUM_EPSILON = 20;
    const MOMENTUM_MAX = 2400;
    let visibilityHandler = null;
    let previewsPausedByDocument = false;
    let scrollLock = null;
    let suppressClicksUntil = 0;
    const pauseReasons = new Set();
    let rafId = null;
    let lastFrameTime = null;
    let activeFullCard = null;
    let activeFullVideo = null;
    let modalFocusActive = false;
    let fullObserver = null;
    // Magnétisme : recentrage de la carte la plus proche du centre lorsque le
    // défilement automatique est temporairement suspendu (scroll manuel / vidéo).
    let magnetActive = false;
    let magnetCard = null;
    const MAGNET_EASE = 0.18;
    let wheelResumeTimer = null;
    let wheelScrollVelocity = 0;
    let inWheelHorizontal = false;
    let sectionObserver = null;
    let pageShowHandler = null;
    let lastViewportWidth =
      viewport.getBoundingClientRect().width || sectionEl.offsetWidth || 0;
    let resizeTimer = null;

    setViewportTouchAction(TOUCH_ACTION_DEFAULT);

    const preventTouchScroll = function (event) {
      if (!pointerDragging || scrollLock !== "horizontal") return;
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    buildGroups();
    preparePreviewVideos(sectionEl);
    queueInitialPreviewAutoplay();
    ensureShopProductImagesLoaded(track);
    updateTargetSpeed();
    applyGroupTransforms();

    const prefersHover = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    );
    if (prefersHover.matches) {
      sectionEl.addEventListener("mouseenter", function () {
        addPause("hover");
      });
      sectionEl.addEventListener("mouseleave", function () {
        removePause("hover");
      });
    }
    viewport.addEventListener("wheel", handleWheel, { passive: false });

    sectionObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            isSectionVisible = true;
            removePause("visibility");
            // Section came back into view — re-kick previews in case the
            // intersection observer pause-loop left them stopped.
            resumePreviewVideos();
            return;
          }

          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          const rootBottom = entry.rootBounds
            ? entry.rootBounds.bottom
            : window.innerHeight || document.documentElement.clientHeight || 0;
          const fullyAbove = entry.boundingClientRect.bottom <= rootTop;
          const fullyBelow = entry.boundingClientRect.top >= rootBottom;
          const fullyOut =
            fullyAbove || fullyBelow || entry.intersectionRatio <= 0;

          if (fullyOut) {
            isSectionVisible = false;
            addPause("visibility");
            stopFullVideo();
          }
        });
      },
      { threshold: 0.0, rootMargin: "0px" }
    );
    sectionObserver.observe(sectionEl);

    visibilityHandler = function () {
      if (document.hidden) {
        handleDocumentHidden();
      } else {
        handleDocumentVisible();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
    pageShowHandler = function () {
      // Triggered on bfcache restore and regular navigation back.
      // Always try to revive previews if document is currently visible.
      if (document.visibilityState !== "hidden") {
        handleDocumentVisible();
      }
    };
    window.addEventListener("pageshow", pageShowHandler);
    if (document.hidden) {
      handleDocumentHidden();
    }

    track.addEventListener("pointerdown", handlePointerDown);
    track.addEventListener("pointermove", handlePointerMove);
    track.addEventListener("pointerup", handlePointerUp);
    track.addEventListener("pointercancel", handlePointerUp);
    viewport.addEventListener("touchmove", preventTouchScroll, {
      passive: false,
    });

    track.addEventListener("click", function (event) {
      if (suppressClicksUntil && getNow() < suppressClicksUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const modalOpener = event.target.closest('[aria-controls^="UgcModal-"]');
      if (modalOpener) {
        event.preventDefault();
        const modalId = modalOpener.getAttribute("aria-controls");
        const modal = modalId ? document.getElementById(modalId) : null;
        if (modal && typeof modal.show === "function") {
          openUgcShopModal(modal, modalOpener);
        }
        return;
      }
      const shopOverlayZone = event.target.closest(
        ".ugc-card--shop .media-card__content, .ugc-card--shop .horizontal-product"
      );
      if (
        shopOverlayZone &&
        !event.target.closest(
          ".horizontal-product__media, .ugc-card__shop-button, .media-card__button"
        )
      ) {
        const shopCard = shopOverlayZone.closest(".ugc-card");
        if (shopCard && shopCard.dataset.hasFull === "true") {
          event.preventDefault();
          toggleFullVideo(shopCard);
          return;
        }
      }
      const playBtn = event.target.closest(".ugc-card__play");
      if (playBtn) {
        event.preventDefault();
        const card = playBtn.closest(".ugc-card");
        toggleFullVideo(card);
        return;
      }
      const previewVideoEl = event.target.closest(".ugc-card__video-preview");
      if (previewVideoEl) {
        const card = previewVideoEl.closest(".ugc-card");
        if (card && card.dataset.hasFull === "true") {
          event.preventDefault();
          toggleFullVideo(card);
        }
        return;
      }
      const posterEl = event.target.closest(".ugc-card__poster");
      if (posterEl) {
        const card = posterEl.closest(".ugc-card");
        if (
          card &&
          card.dataset.hasFull === "true" &&
          card.dataset.hasPreview !== "true"
        ) {
          event.preventDefault();
          toggleFullVideo(card);
          return;
        }
      }
      const wrapperEl = event.target.closest(".ugc-card__video-wrapper");
      if (wrapperEl) {
        const card = wrapperEl.closest(".ugc-card");
        if (
          card &&
          card.dataset.hasFull === "true" &&
          card.dataset.hasPreview !== "true"
        ) {
          event.preventDefault();
          toggleFullVideo(card);
          return;
        }
      }
      const fullVideoEl = event.target.closest(".ugc-card__video-full");
      if (fullVideoEl && activeFullCard) {
        const targetCard = fullVideoEl.closest(".ugc-card");
        if (targetCard) {
          event.preventDefault();
          stopFullVideo();
        }
      }
    });

    sectionEl.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && activeFullCard) {
        stopFullVideo();
      }
    });

    window.addEventListener("resize", handleResize);
    bindUgcShopModals();
    if (sectionRoot) {
      sectionRoot.addEventListener(
        "click",
        function (event) {
          const feedBtn = event.target.closest(
            'button[is="social-feed-button"][aria-controls^="UgcModal-"]'
          );
          if (!feedBtn) return;
          const modalId = feedBtn.getAttribute("aria-controls");
          const modal = modalId ? document.getElementById(modalId) : null;
          if (!modal) return;
          if (!modalFocusActive) {
            enterModalFocusMode();
          }
          startModalVideo(modal);
        },
        true
      );
    }
    ensureLoopRunning();

    sectionEl._ugcCleanup = function () {
      stopFullVideo();
      if (modalFocusActive) {
        exitModalFocusMode();
      }
      cancelMagnet();
      pauseReasons.clear();
      stopLoop();
      window.removeEventListener("resize", handleResize);
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
        visibilityHandler = null;
      }
      if (pageShowHandler) {
        window.removeEventListener("pageshow", pageShowHandler);
        pageShowHandler = null;
      }
      if (wheelResumeTimer) {
        clearTimeout(wheelResumeTimer);
        wheelResumeTimer = null;
      }
      wheelScrollVelocity = 0;
      viewport.removeEventListener("wheel", handleWheel);
      inWheelHorizontal = false;
      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }
      if (previewObserver) {
        previewObserver.disconnect();
        previewObserver = null;
      }
      if (sectionObserver) {
        sectionObserver.disconnect();
        sectionObserver = null;
      }
      if (resizeTimer) {
        clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      track.removeEventListener("dragstart", preventMediaDrag);
      viewport.removeEventListener("touchmove", preventTouchScroll);
      if (sectionEl._ugcEditorCardHost) {
        try {
          sectionEl._ugcEditorCardHost.remove();
        } catch (e) {
          /* ignore */
        }
        sectionEl._ugcEditorCardHost = null;
      }
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);
      scrollLock = null;
      pointerActive = false;
      pointerDragging = false;
      pointerTotalTravelX = 0;
      pointerVelocityX = 0;
      momentumVelocity = 0;
      suppressClicksUntil = 0;
      groupsState = [];
      previewsPausedByDocument = false;
    };

    function handleResize() {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(applyResize, 160);
    }

    function applyResize() {
      resizeTimer = null;
      const newViewportWidth =
        viewport.getBoundingClientRect().width || sectionEl.offsetWidth || 0;
      if (Math.abs(newViewportWidth - lastViewportWidth) < 1) {
        return;
      }
      lastViewportWidth = newViewportWidth;
      const sampleCard = groupsState[0]?.el?.querySelector(".ugc-card");
      if (sampleCard) {
        const rect = sampleCard.getBoundingClientRect();
        mediaWidth = rect.width;
        mediaHeight = rect.height;
      }
      stopFullVideo();
      buildGroups();
      preparePreviewVideos(sectionEl);
      queueInitialPreviewAutoplay();
      ensureShopProductImagesLoaded(track);
      applyGroupTransforms();
      updateTargetSpeed();
    }

    function handlePointerDown(event) {
      if (typeof event.button !== "undefined" && event.button !== 0) {
        return;
      }
      cancelMagnet();
      ensureLoopRunning();
      pointerActive = true;
      pointerDragging = false;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      lastPointerX = event.clientX;
      pointerStartY = event.clientY;
      lastPointerY = event.clientY;
      pointerLastTime = getNow();
      pointerVelocityX = 0;
      pointerTotalTravelX = 0;
      momentumVelocity = 0;
      removePause("momentum");
      scrollLock = null;
      inWheelHorizontal = false;
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);
      track.classList.add("is-pointer-down");
    }

    function handlePointerMove(event) {
      if (!pointerActive || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - lastPointerX;
      const deltaY = event.clientY - lastPointerY;
      const now = getNow();
      const deltaTime = Math.max((now - pointerLastTime) / 1000, 0.001);
      pointerLastTime = now;
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;

      if (!pointerDragging) {
        const totalDeltaX = event.clientX - pointerStartX;
        const totalDeltaY = event.clientY - pointerStartY;
        pointerTotalTravelX = Math.abs(totalDeltaX);
        if (
          Math.abs(totalDeltaX) > 6 &&
          Math.abs(totalDeltaX) > Math.abs(totalDeltaY)
        ) {
          pointerDragging = true;
          addPause("drag");
          track.setPointerCapture(pointerId);
          pointerCaptured = true;
          scrollLock = "horizontal";
          pointerVelocityX = 0;
          momentumVelocity = 0;
          sectionEl.classList.add("ugc--is-dragging");
          setViewportTouchAction(TOUCH_ACTION_LOCKED);
          if (event.pointerType === "touch" && event.cancelable) {
            event.preventDefault();
          }
        } else if (
          Math.abs(totalDeltaY) > 6 &&
          Math.abs(totalDeltaY) > Math.abs(totalDeltaX)
        ) {
          scrollLock = "vertical";
          track.classList.remove("is-pointer-down");
          pointerActive = false;
          pointerDragging = false;
          pointerId = null;
          if (pointerCaptured) {
            track.releasePointerCapture(event.pointerId);
            pointerCaptured = false;
          }
          sectionEl.classList.remove("ugc--is-dragging");
          setViewportTouchAction(TOUCH_ACTION_DEFAULT);
          pointerVelocityX = 0;
          pointerTotalTravelX = 0;
          momentumVelocity = 0;
          removePause("momentum");
          scrollLock = null;
          return;
        }
      } else if (
        scrollLock !== "horizontal" &&
        Math.abs(deltaY) > Math.abs(deltaX) &&
        Math.abs(deltaY) > 2
      ) {
        // Switch to vertical if user changes intent mid-gesture
        scrollLock = "vertical";
        track.classList.remove("is-pointer-down");
        pointerActive = false;
        pointerDragging = false;
        pointerId = null;
        if (pointerCaptured) {
          track.releasePointerCapture(event.pointerId);
          pointerCaptured = false;
        }
        sectionEl.classList.remove("ugc--is-dragging");
        setViewportTouchAction(TOUCH_ACTION_DEFAULT);
        pointerVelocityX = 0;
        pointerTotalTravelX = 0;
        momentumVelocity = 0;
        removePause("momentum");
        scrollLock = null;
        return;
      }

      if (pointerDragging) {
        if (event.pointerType === "touch" && event.cancelable) {
          event.preventDefault();
        }
        position += deltaX;
        normalizePosition();
        applyGroupTransforms();
        pointerVelocityX = deltaX / deltaTime;
        pointerTotalTravelX += Math.abs(deltaX);
      }
    }

    function handlePointerUp(event) {
      if (!pointerActive || event.pointerId !== pointerId) return;
      track.classList.remove("is-pointer-down");

      if (pointerDragging) {
        removePause("drag");
        const flickVelocity = Math.max(
          Math.min(pointerVelocityX, MOMENTUM_MAX),
          -MOMENTUM_MAX
        );
        if (Math.abs(flickVelocity) > MOMENTUM_EPSILON) {
          momentumVelocity = flickVelocity;
          addPause("momentum");
          ensureLoopRunning();
        } else {
          momentumVelocity = 0;
          removePause("momentum");
          maybeMagnetizeAfterSettle();
          stopLoopIfIdle();
        }
        if (pointerTotalTravelX > 12) {
          suppressClicksUntil = getNow() + 350;
        }
      } else {
        momentumVelocity = 0;
        removePause("momentum");
        stopLoopIfIdle();
      }

      pointerActive = false;
      pointerDragging = false;
      pointerId = null;
      if (pointerCaptured) {
        track.releasePointerCapture(event.pointerId);
        pointerCaptured = false;
      }
      scrollLock = null;
      pointerVelocityX = 0;
      pointerTotalTravelX = 0;
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);
    }

    function handleWheel(event) {
      if (!groupSpan || !groupsState.length) return;
      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);

      if (!inWheelHorizontal && absY > absX) {
        return;
      }

      // Trackpads génèrent souvent des deltas horizontaux faibles (1-5 px) sur
      // les premiers événements d'un swipe. Si on attend > 15 et > 2× vertical,
      // l'utilisateur doit insister longtemps avant que le slider ne bouge.
      // Seuils abaissés : 3 px minimum, et axe horizontal simplement dominant.
      const horizontalIntent = inWheelHorizontal
        ? absX > 0 || absX + absY < 0.5
        : absX > 3 && absX >= absY;

      if (!horizontalIntent) {
        inWheelHorizontal = false;
        if (!pointerActive && !pointerDragging) {
          sectionEl.classList.remove("ugc--is-dragging");
          setViewportTouchAction(TOUCH_ACTION_DEFAULT);
        }
        return;
      }

      let delta = event.deltaX;
      if (event.deltaMode === 1) {
        delta *= 30;
      }
      if (!delta) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      cancelMagnet();
      wheelScrollVelocity = wheelScrollVelocity * 0.7 + (-delta * 60) * 0.3;

      inWheelHorizontal = true;
      sectionEl.classList.add("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_LOCKED);
      addPause("wheel");
      position -= delta;
      normalizePosition();
      applyGroupTransforms();
      if (wheelResumeTimer) clearTimeout(wheelResumeTimer);
      wheelResumeTimer = setTimeout(function () {
        const flickVelocity = Math.max(
          Math.min(wheelScrollVelocity, MOMENTUM_MAX),
          -MOMENTUM_MAX
        );
        wheelScrollVelocity = 0;
        removePause("wheel");
        inWheelHorizontal = false;
        if (Math.abs(flickVelocity) > MOMENTUM_EPSILON) {
          momentumVelocity = flickVelocity;
          addPause("momentum");
          ensureLoopRunning();
        } else {
          maybeMagnetizeAfterSettle();
        }
        if (!pointerActive && !pointerDragging) {
          sectionEl.classList.remove("ugc--is-dragging");
          setViewportTouchAction(TOUCH_ACTION_DEFAULT);
        }
      }, 160);
    }

    function buildGroups() {
      groupsState = [];
      totalSpan = 0;
      track.innerHTML = "";

      const sectionWidth = viewport.getBoundingClientRect().width || 0;
      lastViewportWidth = sectionWidth || lastViewportWidth;
      if (!mediaWidth) {
        const sample = baseCards[0];
        if (sample) {
          const rect = sample.getBoundingClientRect();
          mediaWidth = rect.width;
          mediaHeight = rect.height;
        } else {
          mediaWidth =
            parseFloat(computed.getPropertyValue("--ugc-media-width")) || 1;
        }
      }

      const sequenceUnit = (mediaWidth + gapValue) * Math.max(1, mediaCount);
      const duplicationRaw = sequenceUnit
        ? (sectionWidth * 0.55) / sequenceUnit
        : 0;
      sectionEl.dataset.ugcDupRatio = duplicationRaw
        ? duplicationRaw.toFixed(4)
        : "0";
      const groupBlueprints = [];

      if (duplicationRaw < 0.5) {
        const evenMediaCount =
          mediaCount >= 2
            ? mediaCount % 2 === 0
              ? mediaCount
              : mediaCount - 1
            : mediaCount;
        const divisor = Math.max(
          1,
          evenMediaCount > 0 ? evenMediaCount : mediaCount
        );
        const secondaryDenominator = (mediaWidth + gapValue) * divisor;
        const secondaryNumerator = sectionWidth * 0.55;
        const secondaryRatio = secondaryDenominator
          ? secondaryNumerator / secondaryDenominator
          : 0;
        sectionEl.dataset.ugcSecondaryRatio = secondaryRatio
          ? secondaryRatio.toFixed(4)
          : "0";
        const shouldSplitSequences = secondaryRatio < 0.25 && mediaCount > 1;
        sectionEl.dataset.ugcSequenceMode = shouldSplitSequences
          ? "split"
          : "full";
        sectionEl.dataset.ugcSplitActive = shouldSplitSequences
          ? "true"
          : "false";
        usingVariableSpans = shouldSplitSequences;

        if (shouldSplitSequences) {
          const splitIndex = Math.ceil(templateCards.length / 2);
          const firstHalf = templateCards.slice(0, splitIndex);
          let secondHalf = templateCards.slice(splitIndex);
          if (!secondHalf.length) {
            secondHalf = templateCards.slice();
          }
          groupBlueprints.push({ sequences: [firstHalf], keyBase: "split-0" });
          groupBlueprints.push({ sequences: [secondHalf], keyBase: "split-1" });
        } else {
          usingVariableSpans = false;
          groupBlueprints.push({
            sequences: [templateCards],
            keyBase: "full-0",
          });
          groupBlueprints.push({
            sequences: [templateCards],
            keyBase: "full-1",
          });
        }
      } else {
        sectionEl.dataset.ugcSequenceMode = "duplicate";
        sectionEl.dataset.ugcSplitActive = "false";
        usingVariableSpans = false;
        const sequencesPerGroup = Math.ceil(Math.max(1, duplicationRaw));
        for (let gi = 0; gi < 3; gi += 1) {
          const sequences = [];
          for (let seq = 0; seq < sequencesPerGroup; seq += 1) {
            sequences.push(templateCards);
          }
          groupBlueprints.push({ sequences: sequences, keyBase: "seq-" + gi });
        }
      }

      const groupCount = groupBlueprints.length;
      const fragment = document.createDocumentFragment();
      const newStates = [];
      const indexOffset = Math.floor(groupCount / 2);

      groupBlueprints.forEach(function (blueprint, gi) {
        const groupEl = document.createElement("div");
        groupEl.className = "ugc-slider__group";
        groupEl.style.gap = gapValue + "px";
        groupEl.style.left = "0";
        groupEl.dataset.groupIndex = String(gi);

        const sequencesForGroup =
          blueprint.sequences && blueprint.sequences.length
            ? blueprint.sequences
            : [templateCards];
        sequencesForGroup.forEach(function (sequenceTemplates, seqIndex) {
          const templatesForSequence =
            sequenceTemplates && sequenceTemplates.length
              ? sequenceTemplates
              : templateCards;
          const keyPrefix =
            (blueprint.keyBase ? blueprint.keyBase + "-" : "seq-" + gi + "-") +
            String(seqIndex);
          appendTemplatesToGroup(
            groupEl,
            templatesForSequence,
            gi,
            seqIndex,
            keyPrefix
          );
        });

        fragment.appendChild(groupEl);
        newStates.push({
          el: groupEl,
          index: gi - indexOffset,
          sequenceId: gi,
          isDuplicate: gi > 0,
        });
      });

      track.appendChild(fragment);

      groupsState = newStates;

      if (usingVariableSpans) {
        groupSpan =
          totalSpan || (mediaWidth + gapValue) * Math.max(1, mediaCount);
        let maxHeight = mediaHeight;
        groupsState.forEach(function (state) {
          const rect = state.el.getBoundingClientRect();
          if (rect && rect.height) {
            maxHeight = Math.max(maxHeight, rect.height);
          }
        });
        if (maxHeight) {
          mediaHeight = maxHeight;
          track.style.height = maxHeight + "px";
        }
        track.style.position = "relative";
        track.style.display = "block";
        reindexGroups();
        groupSpan = totalSpan || groupSpan;
        position = 0;
        applyGroupTransforms();
        return;
      }

      const firstGroupRect = newStates[0]?.el.getBoundingClientRect();
      const fallbackCardCount =
        (groupBlueprints[0]?.sequences || []).reduce(function (
          total,
          seqTemplates
        ) {
          return total + (seqTemplates ? seqTemplates.length : 0);
        },
        0) || mediaCount;
      groupSpan = firstGroupRect
        ? firstGroupRect.width + gapValue
        : (mediaWidth + gapValue) * Math.max(1, fallbackCardCount);
      if (!groupSpan) {
        groupSpan = (mediaWidth + gapValue) * Math.max(1, fallbackCardCount);
      }

      const groupHeight = firstGroupRect?.height || mediaHeight;
      if (groupHeight) {
        mediaHeight = groupHeight;
        track.style.height = groupHeight + "px";
      }
      track.style.position = "relative";
      track.style.display = "block";

      totalSpan = groupSpan * Math.max(1, groupCount);

      reindexGroups();
      position = 0;
      applyGroupTransforms();
    }

    function appendTemplatesToGroup(
      groupEl,
      templates,
      groupIndex,
      sequenceIndex,
      keyPrefix
    ) {
      const sequenceLabel = String(sequenceIndex);
      const keyLabel = String(keyPrefix);
      templates.forEach(function (template, localIndex) {
        const cardClone = template.cloneNode(true);
        cleanCardNode(cardClone);
        cardClone.dataset.sequenceIndex = sequenceLabel;
        cardClone.dataset.groupIndex = String(groupIndex);
        const baseKey = cardClone.dataset.cardIndex
          ? cardClone.dataset.cardIndex + "-"
          : "";
        cardClone.dataset.sequenceKey =
          baseKey + keyLabel + "-" + String(localIndex);
        groupEl.appendChild(cardClone);
      });
    }

    function updateDynamicMetrics() {
      if (!groupsState.length) {
        totalSpan = 0;
        return;
      }
      let cumulative = 0;
      let newTotal = 0;
      groupsState.forEach(function (state) {
        const rect = state.el.getBoundingClientRect();
        const width =
          rect && rect.width
            ? rect.width
            : (mediaWidth + gapValue) *
              Math.max(1, state.el.children.length || 1);
        state.width = width;
        state.span = width + gapValue;
        state.offset = cumulative;
        cumulative += state.span;
        newTotal += state.span;
      });
      totalSpan = newTotal || (mediaWidth + gapValue) * Math.max(1, mediaCount);
    }

    function ensureShopProductImagesLoaded(scope) {
      if (!scope) return;
      scope
        .querySelectorAll(".ugc-card--shop picture.media img")
        .forEach(function (img) {
          const media = img.closest(".media");
          const finalize = function () {
            img.classList.add("loaded");
            if (media) {
              media.classList.remove("loading");
            }
          };
          if (img.complete && img.naturalWidth > 0) {
            finalize();
            return;
          }
          if (img.dataset.ugcLoadBound === "true") {
            return;
          }
          img.dataset.ugcLoadBound = "true";
          img.addEventListener("load", finalize, { once: true });
          img.addEventListener("error", function () {
            if (media) {
              media.classList.remove("loading");
            }
          }, { once: true });
        });
    }

    function cleanCardNode(clone) {
      clone.classList.add("ugc-card--clone");
      clone.removeAttribute("data-base");
      clone.removeAttribute("data-block-id");
      clone.removeAttribute("data-block-order");
      clone.removeAttribute("data-shopify-editor-block-id");
      clone.removeAttribute("data-shopify-editor-block");
      clone.querySelectorAll("[data-block-id]").forEach(function (el) {
        el.removeAttribute("data-block-id");
      });
      clone.querySelectorAll("[data-block-order]").forEach(function (el) {
        el.removeAttribute("data-block-order");
      });
      clone
        .querySelectorAll("[data-shopify-editor-block-id]")
        .forEach(function (el) {
          el.removeAttribute("data-shopify-editor-block-id");
        });
      clone
        .querySelectorAll("[data-shopify-editor-block]")
        .forEach(function (el) {
          el.removeAttribute("data-shopify-editor-block");
        });
      ensureShopProductImagesLoaded(clone);
    }

    function addPause(reason) {
      if (!pauseReasons.has(reason)) {
        pauseReasons.add(reason);
        updateTargetSpeed();
      }
    }

    function removePause(reason) {
      if (pauseReasons.delete(reason)) {
        updateTargetSpeed();
      }
    }

    function updateTargetSpeed() {
      if (!autoSpeed) {
        currentSpeed = 0;
        targetSpeed = 0;
        stopLoopIfIdle();
        return;
      }
      const paused = pauseReasons.size > 0;
      targetSpeed = paused ? 0 : -autoSpeed;
      if (!paused) {
        cancelMagnet();
        ensureLoopRunning();
      } else {
        stopLoopIfIdle();
      }
    }

    function ensureLoopRunning() {
      if (rafId) {
        return;
      }
      lastFrameTime = null;
      rafId = requestAnimationFrame(step);
    }

    function stopLoop() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastFrameTime = null;
      momentumVelocity = 0;
      removePause("momentum");
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);
    }

    function shouldContinueLoop() {
      if (magnetActive) return true;
      if (autoSpeed && pauseReasons.size === 0) return true;
      if (Math.abs(currentSpeed) > 0.01) return true;
      if (Math.abs(momentumVelocity) > 1) return true;
      if (pointerActive || pointerDragging) return true;
      return false;
    }

    function stopLoopIfIdle() {
      if (!rafId) return;
      if (shouldContinueLoop()) return;
      cancelAnimationFrame(rafId);
      rafId = null;
      lastFrameTime = null;
    }

    // Le magnétisme ne s'applique que lorsque le slider reste à l'arrêt :
    // soit aucun défilement auto (autoSpeed === 0), soit une pause encore
    // active (survol, vidéo…). Si le défilement infini doit reprendre, on
    // n'aimante pas et le magnétisme se retire de lui-même.
    function shouldMagnetize() {
      return autoSpeed === 0 || pauseReasons.size > 0;
    }

    function maybeMagnetizeAfterSettle() {
      if (shouldMagnetize()) {
        startMagnet();
      }
    }

    function computeDeltaToCenter(card) {
      if (!card) return 0;
      const cardRect = card.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      return viewportCenter - cardCenter;
    }

    function findCardNearestCenter() {
      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let best = null;
      let bestDistance = Infinity;
      track.querySelectorAll(".ugc-card").forEach(function (card) {
        const rect = card.getBoundingClientRect();
        if (!rect.width) return;
        const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = card;
        }
      });
      return best;
    }

    function startMagnet(targetCard) {
      if (!groupsState.length) return;
      const card = targetCard || findCardNearestCenter();
      if (!card) return;
      magnetCard = card;
      magnetActive = true;
      ensureLoopRunning();
    }

    function cancelMagnet() {
      magnetActive = false;
      magnetCard = null;
    }

    function step(timestamp) {
      if (!lastFrameTime) {
        lastFrameTime = timestamp;
      }
      const delta = (timestamp - lastFrameTime) / 1000;
      lastFrameTime = timestamp;

      if (magnetActive) {
        if (!magnetCard || !magnetCard.isConnected) {
          magnetActive = false;
          magnetCard = null;
        } else {
          const distanceToCenter = computeDeltaToCenter(magnetCard);
          if (Math.abs(distanceToCenter) < 0.5) {
            position += distanceToCenter;
            magnetActive = false;
            magnetCard = null;
          } else {
            const factor = Math.min(1, MAGNET_EASE * delta * 60);
            position += distanceToCenter * factor;
          }
          normalizePosition();
          applyGroupTransforms();
          if (shouldContinueLoop()) {
            rafId = requestAnimationFrame(step);
          } else {
            rafId = null;
            lastFrameTime = null;
          }
          return;
        }
      }

      if (autoSpeed) {
        currentSpeed += (targetSpeed - currentSpeed) * ease;
        if (Math.abs(currentSpeed) < 0.001) {
          currentSpeed = 0;
        }
      }

      let appliedSpeed = currentSpeed;
      if (momentumVelocity) {
        appliedSpeed += momentumVelocity;
        const decay = Math.pow(MOMENTUM_DECAY, delta * 60);
        momentumVelocity *= decay;
        if (Math.abs(momentumVelocity) < MOMENTUM_EPSILON) {
          momentumVelocity = 0;
          removePause("momentum");
          maybeMagnetizeAfterSettle();
        }
      }

      position += appliedSpeed * delta;

      normalizePosition();
      applyGroupTransforms();

      if (shouldContinueLoop()) {
        rafId = requestAnimationFrame(step);
      } else {
        rafId = null;
        lastFrameTime = null;
      }
    }

    function normalizePosition() {
      if (!groupsState.length) return;
      if (usingVariableSpans) {
        normalizePositionDynamic();
        return;
      }
      if (!groupSpan) return;
      while (position <= -groupSpan) {
        position += groupSpan;
        shiftGroupsLeft();
      }
      while (position >= groupSpan) {
        position -= groupSpan;
        shiftGroupsRight();
      }
    }

    function normalizePositionDynamic() {
      if (!groupsState.length) return;
      let firstSpan = groupsState[0]?.span || 0;
      while (firstSpan && position <= -firstSpan) {
        position += firstSpan;
        shiftGroupsLeft();
        firstSpan = groupsState[0]?.span || 0;
      }
      let lastSpan = groupsState[groupsState.length - 1]?.span || 0;
      while (lastSpan && position >= lastSpan) {
        position -= lastSpan;
        shiftGroupsRight();
        lastSpan = groupsState[groupsState.length - 1]?.span || 0;
      }
    }

    function applyGroupTransforms() {
      if (!groupsState.length) return;
      if (usingVariableSpans) {
        applyGroupTransformsDynamic();
        return;
      }
      groupsState.forEach(function (state) {
        const x = state.index * groupSpan + position;
        state.el.style.transform = "translate3d(" + x + "px, 0, 0)";
      });
      if (rebalanceGroups()) {
        groupsState.forEach(function (state) {
          const x = state.index * groupSpan + position;
          state.el.style.transform = "translate3d(" + x + "px, 0, 0)";
        });
      }
    }

    function applyGroupTransformsDynamic() {
      if (!totalSpan) return;
      let centerOffset = totalSpan / 2;
      groupsState.forEach(function (state) {
        const x = position + (state.offset || 0) - centerOffset;
        state.el.style.transform = "translate3d(" + x + "px, 0, 0)";
      });
      if (rebalanceGroupsDynamic()) {
        centerOffset = totalSpan / 2;
        groupsState.forEach(function (state) {
          const x = position + (state.offset || 0) - centerOffset;
          state.el.style.transform = "translate3d(" + x + "px, 0, 0)";
        });
      }
    }

    function rebalanceGroupsDynamic() {
      const count = groupsState.length;
      if (!count) {
        return false;
      }
      if (count !== 2) {
        updateDynamicMetrics();
        return false;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      const entries = groupsState.map(function (state) {
        const rect = state.el.getBoundingClientRect();
        return {
          state: state,
          rect: rect,
          center: rect.left + rect.width / 2,
          translate: extractTranslateX(state.el),
        };
      });

      const primaryEntry = entries.reduce(function (closest, current) {
        const closestDelta = Math.abs(closest.center - viewportCenter);
        const currentDelta = Math.abs(current.center - viewportCenter);
        return currentDelta < closestDelta ? current : closest;
      }, entries[0]);

      const duplicateEntry =
        entries.find(function (entry) {
          return entry !== primaryEntry;
        }) || primaryEntry;

      if (!primaryEntry || !duplicateEntry) {
        updateDynamicMetrics();
        return false;
      }

      const deltaToCenter = primaryEntry.center - viewportCenter;
      const centerDeadzone = 8;
      if (Math.abs(deltaToCenter) <= centerDeadzone) {
        updateDynamicMetrics();
        return false;
      }

      const primaryLeftOfCenter = deltaToCenter < 0;
      const desiredOrder = primaryLeftOfCenter
        ? [primaryEntry.state, duplicateEntry.state]
        : [duplicateEntry.state, primaryEntry.state];

      if (
        desiredOrder[0] === groupsState[0] &&
        desiredOrder[1] === groupsState[1]
      ) {
        updateDynamicMetrics();
        return false;
      }

      const preservedTranslate = primaryEntry.translate;
      groupsState = desiredOrder;
      reindexGroups();
      syncPositionToDynamicState(primaryEntry.state, preservedTranslate);
      return true;
    }

    function syncPositionToDynamicState(state, translateOverride) {
      if (
        !state ||
        typeof translateOverride !== "number" ||
        !isFinite(translateOverride)
      ) {
        updateDynamicMetrics();
        return;
      }
      const centerOffset = totalSpan ? totalSpan / 2 : 0;
      const offset = state.offset || 0;
      position = translateOverride - offset + centerOffset;
    }

    function shiftGroupsLeft() {
      const first = groupsState.shift();
      if (!first) return;
      groupsState.push(first);
      reindexGroups();
    }

    function shiftGroupsRight() {
      const last = groupsState.pop();
      if (!last) return;
      groupsState.unshift(last);
      reindexGroups();
    }

    function reindexGroups() {
      const count = groupsState.length;
      if (!count) return;
      if (usingVariableSpans) {
        groupsState.forEach(function (state, idx) {
          state.index = idx;
        });
        updateDynamicMetrics();
        return;
      }
      if (count % 2 === 1) {
        const midpoint = (count - 1) / 2;
        groupsState.forEach(function (state, idx) {
          state.index = idx - midpoint;
        });
      } else {
        const midpoint = count / 2;
        groupsState.forEach(function (state, idx) {
          state.index = idx - midpoint + 0.5;
        });
      }
    }

    function rebalanceGroups() {
      if (usingVariableSpans) {
        return false;
      }
      const count = groupsState.length;
      if (!count) return false;
      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;

      const entries = groupsState.map(function (state) {
        const rect = state.el.getBoundingClientRect();
        return {
          state: state,
          rect: rect,
          center: rect.left + rect.width / 2,
        };
      });

      if (count === 3) {
        const sorted = entries.slice().sort(function (a, b) {
          return a.center - b.center;
        });

        const centralEntry = sorted.reduce(function (closest, current) {
          const closestDelta = Math.abs(closest.center - viewportCenter);
          const currentDelta = Math.abs(current.center - viewportCenter);
          return currentDelta < closestDelta ? current : closest;
        }, sorted[0]);

        const centralIdx = sorted.indexOf(centralEntry);
        if (centralIdx !== -1) {
          const leftEntry = sorted[(centralIdx - 1 + count) % count];
          const rightEntry = sorted[(centralIdx + 1) % count];
          if (leftEntry && rightEntry) {
            const desired = [leftEntry.state, centralEntry.state, rightEntry.state];
            if (
              desired[0] === groupsState[0] &&
              desired[1] === groupsState[1] &&
              desired[2] === groupsState[2]
            ) {
              return false;
            }
            groupsState = desired;
            reindexGroups();
            syncPositionToEntry(centralEntry);
            return true;
          }
        }
      } else if (count === 2) {
        const primaryEntry = entries.reduce(function (closest, current) {
          const closestDelta = Math.abs(closest.center - viewportCenter);
          const currentDelta = Math.abs(current.center - viewportCenter);
          return currentDelta < closestDelta ? current : closest;
        }, entries[0]);
        const duplicateEntry =
          entries.find(function (entry) {
            return entry !== primaryEntry;
          }) || primaryEntry;

        if (primaryEntry && duplicateEntry) {
          const deltaToCenter = primaryEntry.center - viewportCenter;
          const centerDeadzone = 8;
          if (Math.abs(deltaToCenter) <= centerDeadzone) {
            return false;
          }
          const primaryLeftOfCenter = deltaToCenter < 0;
          const desired = primaryLeftOfCenter
            ? [primaryEntry.state, duplicateEntry.state]
            : [duplicateEntry.state, primaryEntry.state];
          if (desired[0] === groupsState[0] && desired[1] === groupsState[1]) {
            return false;
          }
          groupsState = desired;
          reindexGroups();
          syncPositionToEntry(primaryEntry);
          return true;
        }
      }

      return false;
    }

    function syncPositionToEntry(entry) {
      if (!entry || !entry.state || !entry.state.el || !groupSpan) {
        return;
      }
      const translateX = extractTranslateX(entry.state.el);
      if (!isFinite(translateX)) {
        return;
      }
      position = translateX - entry.state.index * groupSpan;
    }

    function extractTranslateX(element) {
      if (!element) return null;

      var translate = parseTranslateValue(
        element.style ? element.style.transform : ""
      );
      if (translate !== null) return translate;

      if (window.getComputedStyle) {
        translate = parseTranslateValue(
          window.getComputedStyle(element).transform
        );
        if (translate !== null) return translate;
      }

      return 0;
    }

    function parseTranslateValue(transformValue) {
      if (!transformValue || transformValue === "none") {
        return 0;
      }

      const translateMatch = transformValue.match(
        /translate3d\((-?\d+(?:\.\d+)?)px/
      );
      if (translateMatch) {
        return parseFloat(translateMatch[1]);
      }

      const matrixMatch = transformValue.match(/matrix\(([^)]+)\)/);
      if (matrixMatch) {
        const parts = matrixMatch[1].split(",");
        if (parts.length >= 6) {
          return parseFloat(parts[4]);
        }
      }

      const matrix3dMatch = transformValue.match(/matrix3d\(([^)]+)\)/);
      if (matrix3dMatch) {
        const parts = matrix3dMatch[1].split(",");
        if (parts.length >= 16) {
          return parseFloat(parts[12]);
        }
      }

      return null;
    }

    function handleDocumentHidden() {
      if (previewsPausedByDocument) return;
      previewsPausedByDocument = true;
      addPause("tab-hidden");
      if (activeFullCard) {
        stopFullVideo();
      }

      // Just pause previews — do NOT toggle display:none or rip out sources.
      // Some browsers evict the decoded buffer when video is hidden, which
      // is exactly what was preventing autoplay from resuming after a long
      // background period. Plain pause() keeps state intact.
      track.querySelectorAll(".ugc-card__video-preview").forEach(function (preview) {
        try {
          preview.pause();
        } catch (error) {
          /* noop */
        }
      });

      stopLoopIfIdle();
    }

    function handleDocumentVisible() {
      if (!previewsPausedByDocument) {
        // Even if we did not flag a pause (e.g. pageshow on bfcache),
        // kick previews in case the browser silently paused them.
        resumePreviewVideos();
        removePause("tab-hidden");
        updateTargetSpeed();
        return;
      }
      previewsPausedByDocument = false;
      removePause("tab-hidden");
      updateTargetSpeed();
      // Force reload + replay in case the browser evicted media data while
      // the tab was hidden for an extended period.
      resumePreviewVideos(true);
    }

    function preparePreviewVideos(root) {
      const trackEl = root.querySelector(".ugc-slider__track");
      if (!trackEl) return;
      const previews = trackEl.querySelectorAll(".ugc-card__video-preview");
      previews.forEach(function (video) {
        if (video.dataset.prepared === "true") return;
        video.dataset.prepared = "true";
        video.muted = true;
        video.loop = false;
        video.playsInline = true;
        if (video.dataset.previewLoopBound !== "true") {
          video.dataset.previewLoopBound = "true";
          video.addEventListener("timeupdate", function () {
            if (video.currentTime >= getPreviewLoopEnd(video)) {
              seekPreviewToLoopStart(video);
            }
          });
        }
        video.setAttribute("playsinline", "");
        video.setAttribute("muted", "");
        video.autoplay = true;
        video.defaultMuted = true;
        video.setAttribute("autoplay", "");
        const wrapper = video.closest(".ugc-card__video-wrapper");
        const markReady = function () {
          syncPreviewLoopPosition(video);
          if (wrapper) {
            wrapper.classList.add("is-ready");
          }
        };
        video.addEventListener("loadeddata", markReady);
        if (previewObserver && observedPreviews) {
          observedPreviews.add(video);
          previewObserver.observe(video);
          if (isSectionVisible && isVideoInViewport(video)) {
            ensurePreviewSource(video);
            loadPreview(video);
            playPreviewWhenReady(video);
          }
        } else {
          ensurePreviewSource(video);
          loadPreview(video);
          playPreviewWhenReady(video);
        }
      });
    }

    function toggleFullVideo(card) {
      if (!card) return;
      if (card === activeFullCard) {
        stopFullVideo();
      } else {
        startFullVideo(card);
      }
    }

    function startFullVideo(card) {
      if (!card || card.dataset.hasFull !== "true") return;
      const wrapper = card.querySelector(".ugc-card__video-wrapper");
      const fullVideo = card.querySelector(".ugc-card__video-full");
      if (!wrapper || !fullVideo) return;

      if (activeFullCard && activeFullCard !== card) {
        stopFullVideo();
      }

      activeFullCard = card;
      activeFullVideo = fullVideo;

      momentumVelocity = 0;
      removePause("momentum");
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);

      addPause("full");
      sectionEl.classList.add("ugc--full-playing");
      card.classList.add("ugc-card--active-full");
      card.classList.remove("ugc-card--preview-paused");
      wrapper.classList.add("playing-full");

      const previewVideo = card.querySelector(".ugc-card__video-preview");
      if (previewVideo) {
        previewVideo.pause();
      }

      pauseOtherPreviews(card);
      startMagnet(card);

      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }
      fullObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            const rootBounds = entry.rootBounds;
            const topLimit = rootBounds ? rootBounds.top : 0;
            const bottomLimit = rootBounds
              ? rootBounds.bottom
              : window.innerHeight || document.documentElement.clientHeight;
            const rect = entry.boundingClientRect;
            const verticallyVisible =
              rect.bottom > topLimit && rect.top < bottomLimit;
            if (!verticallyVisible) {
              stopFullVideo();
            }
          });
        },
        { threshold: 0.0 }
      );
      fullObserver.observe(fullVideo);

      ensureFullVideoSources(fullVideo);
      fullVideo.load();
      fullVideo.loop = false;
      fullVideo.controls = false;
      fullVideo.currentTime = 0;
      fullVideo.muted = false;
      fullVideo.defaultMuted = false;
      fullVideo.volume = 1;
      fullVideo.removeAttribute("muted");

      const handleEnded = function () {
        fullVideo.removeEventListener("ended", handleEnded);
        stopFullVideo();
      };
      fullVideo._ugcHandleEnded = handleEnded;
      fullVideo.addEventListener("ended", handleEnded);

      const playPromise = fullVideo.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(function () {
          const resume = function () {
            fullVideo.play().catch(function () {});
            fullVideo.removeEventListener("pointerdown", resume);
          };
          fullVideo.addEventListener("pointerdown", resume);
        });
      }
    }

    function pauseOtherPreviews(activeCard) {
      track.querySelectorAll(".ugc-card").forEach(function (card) {
        if (card === activeCard) return;
        if (card.dataset.hasPreview !== "true") return;
        const preview = card.querySelector(".ugc-card__video-preview");
        if (preview) {
          preview.pause();
          seekPreviewToLoopStart(preview);
        }
        card.classList.add("ugc-card--preview-paused");
      });
    }

    function stopFullVideo() {
      if (!activeFullCard) {
        return;
      }
      const wrapper = activeFullCard.querySelector(".ugc-card__video-wrapper");
      const fullVideo = activeFullVideo;
      const previewVideo = activeFullCard.querySelector(
        ".ugc-card__video-preview"
      );

      if (fullObserver) {
        fullObserver.disconnect();
        fullObserver = null;
      }

      if (fullVideo) {
        if (fullVideo._ugcHandleEnded) {
          fullVideo.removeEventListener("ended", fullVideo._ugcHandleEnded);
          delete fullVideo._ugcHandleEnded;
        }
        fullVideo.pause();
        fullVideo.currentTime = 0;
      }

      if (wrapper) {
        wrapper.classList.remove("playing-full");
      }

      if (previewVideo) {
        requestAnimationFrame(function () {
          requestPreviewPlay(previewVideo);
        });
      }

      sectionEl.classList.remove("ugc--full-playing");
      activeFullCard.classList.remove("ugc-card--active-full");
      activeFullCard.classList.remove("ugc-card--preview-paused");

      activeFullCard = null;
      activeFullVideo = null;

      cancelMagnet();
      removePause("full");
      momentumVelocity = 0;
      removePause("momentum");
      sectionEl.classList.remove("ugc--is-dragging");
      setViewportTouchAction(TOUCH_ACTION_DEFAULT);
      resumePreviewVideos();
      stopLoopIfIdle();
    }

    function resumePreviewVideos(forceReload) {
      if (previewsPausedByDocument) {
        return;
      }
      track.querySelectorAll(".ugc-card").forEach(function (card) {
        card.classList.remove("ugc-card--preview-paused");
        if (card.dataset.hasPreview !== "true") return;
        const preview = card.querySelector(".ugc-card__video-preview");
        if (!preview) return;
        ensurePreviewSource(preview);
        if (forceReload) {
          forcePreviewReload(preview);
        }
        if (!previewObserver) {
          loadPreview(preview);
          playPreviewWhenReady(preview);
        } else if (isVideoInViewport(preview)) {
          loadPreview(preview);
          playPreviewWhenReady(preview);
        }
      });
    }

    function startVisiblePreviewPlayback() {
      if (previewsPausedByDocument) {
        return;
      }
      track.querySelectorAll(".ugc-card").forEach(function (card) {
        if (card.dataset.hasPreview !== "true") return;
        const preview = card.querySelector(".ugc-card__video-preview");
        if (!preview) return;
        if (!previewObserver || isVideoInViewport(preview)) {
          ensurePreviewSource(preview);
          loadPreview(preview);
          playPreviewWhenReady(preview);
        }
      });
    }

    function queueInitialPreviewAutoplay() {
      if (previewsPausedByDocument) {
        return;
      }
      requestAnimationFrame(function () {
        if (previewsPausedByDocument) {
          return;
        }
        startVisiblePreviewPlayback();
      });
    }

    function playPreviewWhenReady(video) {
      if (!video) return;
      if (video.readyState >= 2) {
        requestPreviewPlay(video);
        return;
      }
      if (video.dataset.playOnReady === "true") return;
      const handleReady = function () {
        video.removeEventListener("loadeddata", handleReady);
        video.removeEventListener("canplay", handleReady);
        delete video.dataset.playOnReady;
        requestPreviewPlay(video);
      };
      video.dataset.playOnReady = "true";
      video.addEventListener("loadeddata", handleReady);
      video.addEventListener("canplay", handleReady);
    }

    function ensurePreviewSource(video) {
      if (!video || video.dataset.sourceAttached === "true") return;
      const src = video.dataset.previewSrc;
      if (!src) return;
      const sourceEl = document.createElement("source");
      sourceEl.src = src;
      sourceEl.type = "video/mp4";
      video.appendChild(sourceEl);
      video.dataset.sourceAttached = "true";
    }

    function loadPreview(video) {
      if (!video || video.dataset.previewLoaded === "true") return;
      video.load();
      video.dataset.previewLoaded = "true";
    }

    function forcePreviewReload(video) {
      if (!video) return;
      // Reset loaded flag and reload so that buffered media evicted by the
      // browser during a long background period is refetched. Cheap because
      // the browser caches the file at the HTTP layer.
      delete video.dataset.previewLoaded;
      try {
        video.load();
        video.dataset.previewLoaded = "true";
      } catch (error) {
        /* noop */
      }
    }

    function requestPreviewPlay(video) {
      if (!video) return;
      syncPreviewLoopPosition(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(function () {
          // Retry once on the next frame — most failures after tab-resume
          // are transient and recover immediately.
          requestAnimationFrame(function () {
            if (!video.paused) return;
            const retry = video.play();
            if (retry && typeof retry.then === "function") {
              retry.catch(function () {
                if (video.dataset.previewResumeBound === "true") return;
                const resume = function () {
                  video.play().catch(function () {});
                  video.removeEventListener("pointerdown", resume);
                  delete video.dataset.previewResumeBound;
                };
                video.dataset.previewResumeBound = "true";
                video.addEventListener("pointerdown", resume);
              });
            }
          });
        });
      }
    }

    function ensureFullVideoSources(video) {
      if (!video || video.dataset.sourceAttached === "true") return;
      const src = video.dataset.fullSrc;
      if (!src) return;
      const sourceEl = document.createElement("source");
      sourceEl.src = src;
      sourceEl.type = "video/mp4";
      video.appendChild(sourceEl);
      video.dataset.sourceAttached = "true";
    }

    function isVideoInViewport(video) {
      if (!video || !viewport) return false;
      const videoRect = video.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const margin = 16;
      return (
        videoRect.right > viewportRect.left - margin &&
        videoRect.left < viewportRect.right + margin &&
        videoRect.bottom > viewportRect.top - margin &&
        videoRect.top < viewportRect.bottom + margin
      );
    }

    function handlePreviewIntersection(entries) {
      if (!isSectionVisible) {
        entries.forEach(function (entry) {
          if (entry.target && typeof entry.target.pause === "function") {
            entry.target.pause();
          }
        });
        return;
      }
      if (previewsPausedByDocument) {
        entries.forEach(function (entry) {
          if (entry.target && typeof entry.target.pause === "function") {
            entry.target.pause();
          }
        });
        return;
      }
      entries.forEach(function (entry) {
        const video = entry.target;
        if (!video) return;
        const card = video.closest(".ugc-card");
        const stillVisible =
          entry.isIntersecting ||
          entry.intersectionRatio > 0 ||
          isVideoInViewport(video);
        if (!stillVisible) {
          video.pause();
          return;
        }
        if (card && card.classList.contains("ugc-card--preview-paused")) {
          video.pause();
          return;
        }
        ensurePreviewSource(video);
        loadPreview(video);
        playPreviewWhenReady(video);
      });
    }

    function setViewportTouchAction(value) {
      if (!viewport) return;
      viewport.style.touchAction = value;
      viewport.style.webkitTouchAction = value;
    }

    function isCoarsePointer() {
      return window.matchMedia("(hover: none), (pointer: coarse)").matches;
    }

    function tagUgcShopModalQuickViews(modal) {
      if (!modal || !ugcShopSectionKey) return;
      modal.dataset.ugcShopSection = ugcShopSectionKey;
      modal.querySelectorAll("quick-view").forEach(function (quickView) {
        quickView.dataset.ugcShopModalId = modal.id;
      });
    }

    function isAnyUgcShopModalOpen() {
      return Boolean(getOpenUgcShopModal());
    }

    function enterModalFocusMode() {
      stopFullVideo();
      if (modalFocusActive) return;
      modalFocusActive = true;
      addPause("modal");
      sectionEl.classList.add("ugc--full-playing");
      pauseOtherPreviews(null);
    }

    function exitModalFocusMode() {
      if (!modalFocusActive) return;
      modalFocusActive = false;
      sectionEl.classList.remove("ugc--full-playing");
      track.querySelectorAll(".ugc-card").forEach(function (card) {
        card.classList.remove("ugc-card--preview-paused");
      });
      removePause("modal");
      resumePreviewVideos();
      stopLoopIfIdle();
    }

    function openUgcShopModal(modal, activator) {
      if (!modal || typeof modal.show !== "function") return;
      enterModalFocusMode();
      // Lecture dans la même tâche synchrone que le clic (jeton geste iOS).
      startModalVideo(modal);
      modal.show(activator || null);
    }

    function bindUgcShopModals() {
      if (!sectionRoot) return;
      sectionRoot.querySelectorAll('[id^="UgcModal-"]').forEach(function (modal) {
        if (modal.dataset.ugcModalBound === "true") return;
        modal.dataset.ugcModalBound = "true";
        tagUgcShopModalQuickViews(modal);
        modal.addEventListener("modal:afterShow", function (event) {
          if (event.target !== modal) return;
          if (!modalFocusActive) {
            enterModalFocusMode();
          }
          attemptModalPlay(getModalVideoMedia(modal));
        });
        modal.addEventListener("modal:afterHide", function (event) {
          if (event.target !== modal) return;
          stopModalVideo(modal);
          if (!isAnyUgcShopModalOpen()) {
            exitModalFocusMode();
          }
        });
        setupModalVideoUI(modal);
      });

      bindUgcModalStackedQuickViews();
    }

    function getOpenUgcShopModal() {
      if (!ugcShopSectionKey) return null;
      return (
        Array.from(document.querySelectorAll('[id^="UgcModal-"][open]')).find(
          function (modal) {
            return modal.dataset.ugcShopSection === ugcShopSectionKey;
          }
        ) || null
      );
    }

    function getUgcShopModalForQuickView(quickView) {
      if (!quickView || !quickView.dataset.ugcShopModalId) return null;
      const modal = document.getElementById(quickView.dataset.ugcShopModalId);
      return modal && modal.open ? modal : null;
    }

    function pauseModalVideoAsUser(modal) {
      const videoMedia = getModalVideoMedia(modal);
      if (!videoMedia) return;
      const video = ensureModalVideoElement(videoMedia);
      const wasPlaying =
        videoMedia.hasAttribute("playing") || Boolean(video && !video.paused);
      videoMedia.dataset.ugcWasPlaying = wasPlaying ? "true" : "false";
      hideModalVideoOverlay(videoMedia);

      if (!wasPlaying) {
        syncModalControlsState(videoMedia);
        return;
      }

      try {
        if (typeof videoMedia.pause === "function") {
          videoMedia.pause();
        }
        if (video) {
          video.pause();
        }
      } catch (error) {
        /* noop */
      }
      syncModalControlsState(videoMedia);
    }

    function bindUgcModalStackedQuickViews() {
      if (document.documentElement.dataset.ugcShopQuickViewBound === "true") {
        return;
      }
      document.documentElement.dataset.ugcShopQuickViewBound = "true";

      const onQuickViewToggle = function (event) {
        const quickView = event.target;
        if (
          !quickView ||
          !quickView.matches ||
          !quickView.matches("quick-view") ||
          !quickView.hasAttribute("open")
        ) {
          return;
        }
        const ugcModal = getUgcShopModalForQuickView(quickView);
        if (!ugcModal) return;
        pauseModalVideoAsUser(ugcModal);
      };

      const onQuickViewHide = function (event) {
        const quickView = event.target;
        if (!quickView || !quickView.matches || !quickView.matches("quick-view")) {
          return;
        }
        const ugcModal = document.getElementById(
          quickView.dataset.ugcShopModalId || ""
        );
        if (!ugcModal || !ugcModal.open) return;
        resumeModalVideoAfterStackedModal(ugcModal);
      };

      document.addEventListener("toggle", onQuickViewToggle, true);
      document.addEventListener("modal:afterHide", onQuickViewHide, true);
    }

    function getModalVideoMedia(modal) {
      return modal ? modal.querySelector("video-media.ugc-shop-modal__video-inner") : null;
    }

    function resumeModalVideoAfterStackedModal(modal) {
      const videoMedia = getModalVideoMedia(modal);
      if (!videoMedia) return;
      const shouldResume = videoMedia.dataset.ugcWasPlaying === "true";
      delete videoMedia.dataset.ugcWasPlaying;
      if (!shouldResume) {
        syncModalControlsState(videoMedia);
        return;
      }
      const video = videoMedia.querySelector("video");
      const posterBtn = videoMedia.querySelector(".deferred-poster");
      if (
        video &&
        video.paused &&
        posterBtn &&
        videoMedia.hasAttribute("loaded")
      ) {
        posterBtn.click();
      } else {
        attemptModalPlay(videoMedia);
      }
      syncModalControlsState(videoMedia);
    }

    function stopModalVideo(modal) {
      const videoMedia = getModalVideoMedia(modal);
      if (!videoMedia) return;
      delete videoMedia.dataset.ugcWasPlaying;
      delete videoMedia._ugcSuppressClickUntil;
      videoMedia.classList.remove(
        "is-overlay-visible",
        "is-overlay-fading"
      );
      if (videoMedia._ugcOverlayHideTimer) {
        clearTimeout(videoMedia._ugcOverlayHideTimer);
        videoMedia._ugcOverlayHideTimer = null;
      }
      if (videoMedia._ugcOverlayFadeTimer) {
        clearTimeout(videoMedia._ugcOverlayFadeTimer);
        videoMedia._ugcOverlayFadeTimer = null;
      }
      videoMedia.removeAttribute("data-ugc-show-controls");
      videoMedia.removeAttribute("data-ugc-paused-ui");
      videoMedia.removeAttribute("data-ugc-playing");
      const nativeVideo = videoMedia.querySelector("video");
      try {
        if (nativeVideo) {
          nativeVideo.pause();
          delete nativeVideo.dataset.ugcModalPlayOnReady;
        }
      } catch (error) {
        /* noop */
      }
      /* playing peut rester après currentTime=0 (seek) : bloque play() au réouverture. */
      videoMedia.removeAttribute("playing");
      try {
        if (typeof videoMedia.pause === "function") {
          videoMedia.pause();
        }
      } catch (error) {
        /* noop */
      }
      videoMedia.removeAttribute("playing");
      if (nativeVideo) {
        try {
          nativeVideo.currentTime = 0;
        } catch (error) {
          /* noop */
        }
      }
      videoMedia.removeAttribute("playing");
      syncModalVideoMediaPlayingAttr(videoMedia, nativeVideo);
      const timeline = videoMedia.querySelector(".ugc-modal-video__timeline");
      if (timeline) {
        setModalTimelineRatio(timeline, 0);
        timeline.disabled = true;
      }
    }

    function ensureModalVideoElement(videoMedia) {
      if (!videoMedia) return null;
      try {
        const templateEl = videoMedia.querySelector("template");
        if (templateEl && !videoMedia.querySelector("video")) {
          templateEl.replaceWith(
            templateEl.content.firstElementChild.cloneNode(true)
          );
          videoMedia.setAttribute("loaded", "");
        }
      } catch (error) {
        /* noop */
      }
      return videoMedia.querySelector("video");
    }

    function configureModalVideoElement(videoEl) {
      if (!videoEl) return;
      videoEl.controls = false;
      videoEl.removeAttribute("controls");
      videoEl.loop = true;
      videoEl.setAttribute("loop", "");
      videoEl.playsInline = true;
      videoEl.setAttribute("playsinline", "");
      videoEl.preload = "auto";
      videoEl.setAttribute(
        "controlslist",
        "nodownload nofullscreen nopictureinpicture noplaybackrate noremoteplayback"
      );
      videoEl.setAttribute("disablepictureinpicture", "");
      videoEl.setAttribute("disableremoteplayback", "");
      // Ouverture via clic produit = clic play : son activé (pas d'autoplay muet).
      videoEl.muted = false;
      videoEl.defaultMuted = false;
      videoEl.volume = 1;
      videoEl.removeAttribute("muted");
    }

    function syncModalVideoMediaPlayingAttr(videoMedia, videoEl) {
      if (!videoMedia) return;
      if (!videoEl || videoEl.paused) {
        videoMedia.removeAttribute("playing");
      }
    }

    function playModalVideoElement(videoEl, videoMedia) {
      if (!videoEl) return;
      configureModalVideoElement(videoEl);
      if (!videoEl.paused) {
        return;
      }
      syncModalVideoMediaPlayingAttr(videoMedia, videoEl);
      if (typeof videoMedia.play === "function") {
        videoMedia.play();
      }
      if (!videoEl.paused) {
        return;
      }
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(function () {
          try {
            videoEl.muted = true;
            videoEl.setAttribute("muted", "");
            videoEl.play().catch(function () {});
          } catch (error) {
            /* noop */
          }
        });
      }
    }

    function attemptModalPlay(videoMedia) {
      if (!videoMedia) return;
      try {
        videoMedia.player; // eslint-disable-line no-unused-expressions
      } catch (error) {
        /* noop */
      }
      const videoEl = ensureModalVideoElement(videoMedia);
      syncModalVideoMediaPlayingAttr(videoMedia, videoEl);
      const timeline = videoMedia.querySelector(".ugc-modal-video__timeline");
      if (timeline) {
        setModalTimelineRatio(timeline, 0);
        timeline.disabled = false;
      }
      syncModalControlsState(videoMedia);
      if (!videoEl) {
        if (typeof videoMedia.play === "function") {
          videoMedia.play();
        }
        return;
      }
      if (!videoEl.paused) {
        syncModalControlsState(videoMedia);
        if (timeline && videoEl.duration && isFinite(videoEl.duration)) {
          setModalTimelineRatio(
            timeline,
            videoEl.currentTime / videoEl.duration
          );
        }
        return;
      }
      configureModalVideoElement(videoEl);
      playModalVideoElement(videoEl, videoMedia);

      if (videoEl.readyState >= 2) {
        return;
      }
      if (videoEl.dataset.ugcModalPlayOnReady === "true") {
        return;
      }
      videoEl.dataset.ugcModalPlayOnReady = "true";

      const playWhenReady = function () {
        if (!videoMedia.isConnected) return;
        const openModal = videoMedia.closest('[id^="UgcModal-"]');
        if (openModal && !openModal.open) return;
        configureModalVideoElement(videoEl);
        playModalVideoElement(videoEl, videoMedia);
      };

      videoEl.addEventListener("loadeddata", playWhenReady, { once: true });
      videoEl.addEventListener("canplay", playWhenReady, { once: true });
      try {
        videoEl.load();
      } catch (error) {
        /* noop */
      }
    }

    function startModalVideo(modal) {
      if (!modal) return;
      attemptModalPlay(getModalVideoMedia(modal));
    }

    function setModalTimelineRatio(timeline, ratio) {
      if (!timeline) return;
      const clamped = Math.min(1, Math.max(0, ratio));
      const pct = (clamped * 100).toFixed(2) + "%";
      const value = String(Math.round(clamped * 1000));
      timeline.value = value;
      timeline.style.setProperty("--ugc-timeline-progress", pct);
      timeline.setAttribute("aria-valuenow", value);
    }

    function isModalTouchUi() {
      return (
        window.matchMedia("(max-width: 1023px)").matches || isCoarsePointer()
      );
    }

    function syncModalControlsState(videoMedia) {
      if (!videoMedia) return;
      const video = videoMedia.querySelector("video");
      videoMedia.removeAttribute("data-ugc-show-controls");
      if (isModalTouchUi() && video && video.paused) {
        videoMedia.setAttribute("data-ugc-paused-ui", "");
      } else {
        videoMedia.removeAttribute("data-ugc-paused-ui");
      }
    }

    var UGC_OVERLAY_IDLE_MS = 2800;
    var UGC_OVERLAY_FADE_MS = 220;

    function clearOverlayHideTimers(videoMedia) {
      if (!videoMedia) return;
      if (videoMedia._ugcOverlayHideTimer) {
        clearTimeout(videoMedia._ugcOverlayHideTimer);
        videoMedia._ugcOverlayHideTimer = null;
      }
      if (videoMedia._ugcOverlayFadeTimer) {
        clearTimeout(videoMedia._ugcOverlayFadeTimer);
        videoMedia._ugcOverlayFadeTimer = null;
      }
    }

    function dismissModalVideoOverlay(videoMedia, animate) {
      if (!videoMedia || !videoMedia.classList.contains("is-overlay-visible")) {
        return;
      }
      clearOverlayHideTimers(videoMedia);
      if (!animate) {
        videoMedia.classList.remove("is-overlay-visible", "is-overlay-fading");
        syncModalControlsState(videoMedia);
        return;
      }
      videoMedia.classList.add("is-overlay-fading");
      videoMedia._ugcOverlayFadeTimer = setTimeout(function () {
        videoMedia.classList.remove(
          "is-overlay-visible",
          "is-overlay-fading"
        );
        videoMedia._ugcOverlayFadeTimer = null;
        syncModalControlsState(videoMedia);
      }, UGC_OVERLAY_FADE_MS);
    }

    function hideModalVideoOverlay(videoMedia) {
      dismissModalVideoOverlay(videoMedia, true);
    }

    function scheduleOverlayAutoHide(videoMedia) {
      if (!videoMedia || !videoMedia.classList.contains("is-overlay-visible")) {
        return;
      }
      clearOverlayHideTimers(videoMedia);
      videoMedia.classList.remove("is-overlay-fading");
      videoMedia._ugcOverlayHideTimer = setTimeout(function () {
        dismissModalVideoOverlay(videoMedia, true);
        videoMedia._ugcOverlayHideTimer = null;
      }, UGC_OVERLAY_IDLE_MS);
    }

    function bumpOverlayInteraction(videoMedia) {
      if (!videoMedia || !isModalTouchUi()) return;
      if (videoMedia.classList.contains("is-overlay-visible")) {
        scheduleOverlayAutoHide(videoMedia);
      }
    }

    function revealModalVideoOverlay(videoMedia) {
      if (!videoMedia) return;
      const video = videoMedia.querySelector("video");
      const timeline = videoMedia.querySelector(".ugc-modal-video__timeline");
      if (timeline) {
        const ratio =
          video && video.duration && isFinite(video.duration)
            ? video.currentTime / video.duration
            : 0;
        setModalTimelineRatio(timeline, ratio);
      }
      clearOverlayHideTimers(videoMedia);
      videoMedia.classList.remove("is-overlay-fading");
      void videoMedia.offsetWidth;
      videoMedia.classList.add("is-overlay-visible");
      scheduleOverlayAutoHide(videoMedia);
    }

    function toggleModalVideoOverlay(videoMedia) {
      if (!videoMedia) return;
      if (videoMedia.classList.contains("is-overlay-visible")) {
        dismissModalVideoOverlay(videoMedia, true);
        return;
      }
      revealModalVideoOverlay(videoMedia);
    }

    function setupModalVideoUI(modal) {
      const videoMedia = getModalVideoMedia(modal);
      if (!videoMedia || videoMedia.dataset.ugcModalUiAttached === "true") {
        return;
      }
      videoMedia.dataset.ugcModalUiAttached = "true";

      const timeline = videoMedia.querySelector(
        "input.ugc-modal-video__timeline"
      );
      let seeking = false;
      let timelineRaf = null;

      const getVideo = function () {
        return videoMedia.querySelector("video");
      };

      const syncTimeline = function () {
        if (!timeline || seeking) return;
        const video = getVideo();
        if (!video || !video.duration || !isFinite(video.duration)) return;
        const ratio = Math.min(
          1,
          Math.max(0, video.currentTime / video.duration)
        );
        setModalTimelineRatio(timeline, ratio);
      };

      const stopTimelineLoop = function () {
        if (timelineRaf) {
          cancelAnimationFrame(timelineRaf);
          timelineRaf = null;
        }
      };

      const startTimelineLoop = function () {
        stopTimelineLoop();
        const tick = function () {
          syncTimeline();
          const video = getVideo();
          if (video && !video.paused && !video.ended) {
            timelineRaf = requestAnimationFrame(tick);
          } else {
            timelineRaf = null;
          }
        };
        timelineRaf = requestAnimationFrame(tick);
      };

      const seekFromTimelineValue = function () {
        const video = getVideo();
        if (!video || !video.duration || !isFinite(video.duration)) return;
        const ratio = Number(timeline.value) / 1000;
        video.currentTime = ratio * video.duration;
        setModalTimelineRatio(timeline, ratio);
      };

      const seekFromClientX = function (clientX) {
        if (!timeline) return;
        const video = getVideo();
        if (!video || !video.duration || !isFinite(video.duration)) return;
        const rect = timeline.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = Math.min(
          1,
          Math.max(0, (clientX - rect.left) / rect.width)
        );
        setModalTimelineRatio(timeline, ratio);
        video.currentTime = ratio * video.duration;
      };

      if (timeline) {
        let timelinePointerId = null;

        const suppressVideoSurfaceClick = function () {
          videoMedia._ugcSuppressClickUntil = Date.now() + 500;
        };

        const endTimelineSeek = function (event) {
          if (!seeking) return;
          if (
            timelinePointerId !== null &&
            event &&
            event.pointerId != null &&
            event.pointerId !== timelinePointerId
          ) {
            return;
          }
          const clientX = event && event.clientX != null ? event.clientX : null;
          if (clientX != null) {
            seekFromClientX(clientX);
          }
          seeking = false;
          timelinePointerId = null;
          videoMedia._ugcTimelineTouch = false;
          suppressVideoSurfaceClick();
          bumpOverlayInteraction(videoMedia);
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
        };

        const onTimelinePointerDown = function (event) {
          if (timelinePointerId !== null) return;
          if (event.pointerType === "mouse" && event.button !== 0) return;
          timelinePointerId = event.pointerId;
          seeking = true;
          videoMedia._ugcTimelineTouch = true;
          try {
            timeline.setPointerCapture(event.pointerId);
          } catch (error) {
            /* noop */
          }
          if (isModalTouchUi()) {
            revealModalVideoOverlay(videoMedia);
          }
          bumpOverlayInteraction(videoMedia);
          seekFromClientX(event.clientX);
          event.preventDefault();
          event.stopPropagation();
        };

        const onTimelinePointerMove = function (event) {
          if (!seeking || event.pointerId !== timelinePointerId) return;
          seekFromClientX(event.clientX);
          bumpOverlayInteraction(videoMedia);
          event.preventDefault();
          event.stopPropagation();
        };

        const blockTimelineNativeEvent = function (event) {
          event.preventDefault();
          event.stopPropagation();
        };

        timeline.addEventListener("pointerdown", onTimelinePointerDown, true);
        timeline.addEventListener("pointermove", onTimelinePointerMove, true);
        timeline.addEventListener("pointerup", endTimelineSeek, true);
        timeline.addEventListener("pointercancel", endTimelineSeek, true);
        timeline.addEventListener("mousedown", blockTimelineNativeEvent, true);
        timeline.addEventListener("click", blockTimelineNativeEvent, true);
        timeline.addEventListener("input", blockTimelineNativeEvent, true);
        timeline.addEventListener("change", blockTimelineNativeEvent, true);
        timeline.addEventListener("keydown", function (event) {
          if (
            event.key !== "ArrowLeft" &&
            event.key !== "ArrowRight" &&
            event.key !== "Home" &&
            event.key !== "End"
          ) {
            return;
          }
          const video = getVideo();
          if (!video || !video.duration || !isFinite(video.duration)) return;
          event.preventDefault();
          let ratio = video.currentTime / video.duration;
          if (event.key === "ArrowLeft") ratio -= 0.05;
          if (event.key === "ArrowRight") ratio += 0.05;
          if (event.key === "Home") ratio = 0;
          if (event.key === "End") ratio = 1;
          ratio = Math.min(1, Math.max(0, ratio));
          video.currentTime = ratio * video.duration;
          setModalTimelineRatio(timeline, ratio);
          bumpOverlayInteraction(videoMedia);
        });
      }

      videoMedia.addEventListener("timeupdate", syncTimeline);
      videoMedia.addEventListener("loadedmetadata", syncTimeline);
      videoMedia.addEventListener("durationchange", syncTimeline);

      const bindVideoEvents = function () {
        const video = getVideo();
        if (!video || video.dataset.ugcModalUiVideoBound === "true") return;
        video.dataset.ugcModalUiVideoBound = "true";
        video.addEventListener("play", function () {
          if (seeking) return;
          videoMedia.setAttribute("data-ugc-playing", "");
          startTimelineLoop();
          hideModalVideoOverlay(videoMedia);
          syncModalControlsState(videoMedia);
          syncTimeline();
        });
        video.addEventListener("playing", function () {
          videoMedia.setAttribute("data-ugc-playing", "");
          startTimelineLoop();
          syncTimeline();
        });
        video.addEventListener("pause", function () {
          videoMedia.removeAttribute("data-ugc-playing");
          stopTimelineLoop();
          syncTimeline();
          syncModalControlsState(videoMedia);
          if (isModalTouchUi()) {
            if (videoMedia.classList.contains("is-overlay-visible")) {
              scheduleOverlayAutoHide(videoMedia);
            }
          } else {
            hideModalVideoOverlay(videoMedia);
          }
        });
        video.addEventListener("ended", function () {
          stopTimelineLoop();
          syncTimeline();
        });
      };

      const observer = new MutationObserver(function () {
        if (getVideo()) {
          bindVideoEvents();
        }
      });
      observer.observe(videoMedia, { childList: true, subtree: true });
      bindVideoEvents();

      const handleVideoSurfaceTap = function (event) {
        if (videoMedia._ugcTimelineTouch || seeking) {
          return;
        }
        if (
          event.target.closest(
            ".play-button, .ugc-modal-video-controls, .ugc-modal-video__timeline"
          )
        ) {
          return;
        }
        const video = getVideo();
        if (!video || !videoMedia.hasAttribute("loaded")) return;

        if (
          videoMedia._ugcSuppressClickUntil &&
          Date.now() < videoMedia._ugcSuppressClickUntil
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (isModalTouchUi()) {
          if (event.type === "click") {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          videoMedia._ugcSuppressClickUntil = Date.now() + 450;
          toggleModalVideoOverlay(videoMedia);
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (video.paused) {
          videoMedia.play();
        } else {
          videoMedia.pause();
        }
      };

      const mediaHitArea =
        modal.classList.contains("ugc-shop-modal--video")
          ? videoMedia.closest(".product__media-container")
          : null;
      const videoSurfaceTarget = mediaHitArea || videoMedia;
      videoSurfaceTarget.addEventListener("touchend", handleVideoSurfaceTap, true);
      videoSurfaceTarget.addEventListener("click", handleVideoSurfaceTap, true);
    }

    function getNow() {
      return typeof performance !== "undefined" &&
        typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll(".ugc-slider").forEach(initUGCSlider);
  });

  document.addEventListener("shopify:section:load", function (event) {
    event.target.querySelectorAll(".ugc-slider").forEach(function (sectionEl) {
      sectionEl.dataset.initialized = "";
      initUGCSlider(sectionEl);
    });
  });

  document.addEventListener("shopify:section:unload", function (event) {
    event.target.querySelectorAll(".ugc-slider").forEach(function (sectionEl) {
      if (sectionEl._ugcCleanup) {
        sectionEl._ugcCleanup();
        delete sectionEl._ugcCleanup;
      }
      sectionEl.dataset.initialized = "";
    });
  });
})();
